/**
 * Stale-while-revalidate via localStorage hydration of selected Redux
 * slices.
 *
 * Pages already mount, dispatch a fetch thunk, then render. The
 * white-screen-of-spinner happens between the mount and the first
 * thunk fulfillment. With this layer:
 *
 *   1. On app boot, we preload Redux state from localStorage. The
 *      next page mount sees the last-known data IMMEDIATELY.
 *   2. The page still dispatches its fetch thunk on mount; when the
 *      fresh response arrives, the reducer replaces the data and
 *      the page re-renders. Often a no-op (data didn't change).
 *   3. After every dispatch (debounced 500 ms), we serialize the
 *      persisted slices back to localStorage so the next reload
 *      also gets the latest cached view.
 *
 * Cache scope: we key by (last-16-of-userToken, activeCompanyId)
 * so:
 *   - A different user on the same browser doesn't see the previous
 *     user's cached jobs/packages.
 *   - Switching companies switches caches naturally instead of
 *     leaking another tenant's data.
 *
 * Cleared on logout (see authSlice).
 */

const PERSIST_PREFIX = 'screeners:store:v1';

// Slices that are expensive to fetch + safe to show stale.
// Auth/company/websocket deliberately not persisted — they drive
// gating + live state and the existing localStorage keys handle them.
const PERSISTED_SLICES = [
  'jobs',
  'dcpJobs',
  'delivery',
  'venues',
  'companies',
  'dcpNodes',
];

// Fields inside each slice that aren't worth caching (e.g. transient
// `loading` flags) — strip them so re-hydration doesn't accidentally
// render the page in a "loading" state. The fetch thunks reset
// loading=true themselves on dispatch, so dropping it here is safe.
const TRANSIENT_KEYS = new Set(['loading', 'error']);

const SAVE_DEBOUNCE_MS = 500;
const MAX_PAYLOAD_BYTES = 4 * 1024 * 1024;  // 4 MiB headroom under the ~5 MiB localStorage cap

function _scopeKey() {
  // Scope by stable USER ID, not by JWT signature — the token rolls
  // every 20 min via the auto-refresh path, which would otherwise
  // invalidate the cache on every refresh and produce the
  // 2s-empty-state-every-load symptom users hit on 2026-05-12.
  // Falls back to the last-16-of-token if the user blob isn't there
  // yet (early in the login flow before /api/users/me populates).
  let uid = 'anon';
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (u && (u.id || u.id === 0)) uid = String(u.id);
  } catch (_e) { /* fall through */ }
  if (uid === 'anon') {
    uid = (localStorage.getItem('userToken') || 'anon').slice(-16);
  }
  const co = localStorage.getItem('activeCompanyId') || '0';
  return `${PERSIST_PREFIX}:u${uid}:${co}`;
}

function _stripTransient(slice) {
  if (!slice || typeof slice !== 'object') return slice;
  const out = {};
  for (const [k, v] of Object.entries(slice)) {
    if (TRANSIENT_KEYS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

/** Read persisted slices for the CURRENT user/company scope. */
export function loadPersistedState() {
  try {
    const raw = localStorage.getItem(_scopeKey());
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return undefined;
    return parsed;
  } catch (_e) {
    return undefined;
  }
}

let _saveTimer = null;
let _lastSerialized = null;
/** Subscribe the store so every state change debounces back to disk.
 *  Returns the Redux unsubscribe handle so a caller (typically a test
 *  or a hot-reload setup) can detach the subscription cleanly. In
 *  production the subscription lives for the tab's lifetime, which is
 *  intentional.
 */
export function attachPersist(store) {
  return store.subscribe(() => {
    if (_saveTimer) return;
    _saveTimer = setTimeout(() => {
      _saveTimer = null;
      try {
        const state = store.getState();
        const slim = {};
        for (const k of PERSISTED_SLICES) {
          if (state[k]) slim[k] = _stripTransient(state[k]);
        }
        const serialized = JSON.stringify(slim);
        // Skip writes when nothing meaningful changed (a lot of
        // dispatches are no-op for these slices).
        if (serialized === _lastSerialized) return;
        if (serialized.length > MAX_PAYLOAD_BYTES) {
          // Bail rather than crash localStorage. Better to lose the
          // cache than to silently block writes for the whole app.
          return;
        }
        localStorage.setItem(_scopeKey(), serialized);
        _lastSerialized = serialized;
      } catch (_e) {
        // localStorage full, JSON cycle, whatever — silent.
      }
    }, SAVE_DEBOUNCE_MS);
  });
}

/** Remove every persisted-store key, any user/company scope. */
export function clearPersist() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PERSIST_PREFIX + ':')) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch (_e) { /* nothing to do */ }
  _lastSerialized = null;
}

// Exposed for tests
export const _internals = { PERSIST_PREFIX, PERSISTED_SLICES, TRANSIENT_KEYS };
